import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import breakpoints from "../../../styles/breakpoints";
import { colors, fonts } from "../../../styles/variables";
import WineRating from "../../atoms/WineRating";

export const WineCardContainer = styled.div<{
  isInCollections: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: relative;
  ${(props) =>
    props.isInCollections
      ? css`
          min-width: 300px;
          max-width: 300px;
          margin-right: 20px;
        `
      : css`
          width: 100%;
          max-width: 350px;
        `};
  overflow: hidden;
  margin-bottom: 20px;
  ${breakpoints.xsDown} {
    ${(props) =>
      props.isInCollections &&
      css`
        min-width: 250px;
        max-width: 250px;
      `}
  }
  ${breakpoints.iphone5} {
    ${(props) =>
      props.isInCollections &&
      css`
        min-width: 230px;
        max-width: 230px;
      `}
  }
`;

export const WineCardFooter = styled.div`
  ${breakpoints.containerLgUp} {
    height: 140px;
  }
  ${breakpoints.containerLgDown} {
    height: 160px;
  }
  ${breakpoints.mdDown} {
    height: 180px;
  }
  cursor: auto;
  border: 1px solid ${colors.black};
  border-top: none;
  padding: 10px;
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

export const TitleAndBookmarkContainer = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
`;

export const MemberInfoLabel = styled.span<{
  isInWineCollections: boolean;
}>`
  display: inline-block;
  font-size: 12px;
  line-height: 14px;
  ${(props) =>
    !props.isInWineCollections &&
    css`
      ${breakpoints.smDown} {
        font-size: 10px;
        line-height: 12px;
      }
    `};
`;

export const LowStockInfoLabel = styled.span<{
  isInWineCollections: boolean;
}>`
  display: inline-block;
  font-family: ${fonts.fontInterstateBlackCompressed};
  color: ${colors.danger};
  font-size: 16px;
  ${(props) =>
    !props.isInWineCollections &&
    css`
      ${breakpoints.smDown} {
        font-size: 15px;
      }
      ${breakpoints.xsDown} {
        font-size: 14px;
      }
    `};
`;

export const WineTitleText = styled.p<{
  addHyphen: boolean;
  isInWineCollections: boolean;
}>`
  font-size: 20px;
  font-family: ${fonts.fontInterstateBlackCompressed};
  cursor: pointer;
  overflow: hidden;
  line-height: 20px;
  max-width: 100%;
  ${breakpoints.containerLgUp} {
    height: 20px;
    white-space: nowrap;
    text-overflow: clip;
  }
  ${breakpoints.containerLgDown} {
    font-size: 20px;
    line-height: 20px;
    display: -webkit-inline-box;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    height: 40px;
    ${(props) =>
      props.addHyphen &&
      css`
        -webkit-hyphens: auto;
        hyphens: auto;
        overflow-wrap: anywhere;
      `};
  }
  ${breakpoints.mdDown} {
    font-size: 19px;
    line-height: 19px;
    height: 38px;
    -webkit-line-clamp: 2;
  }
  ${(props) =>
    !props.isInWineCollections &&
    css`
      ${breakpoints.xsDown} {
        font-size: 18px;
        line-height: 19px;
        height: 38px;
        -webkit-line-clamp: 2;
      }
    `};
`;

export const TagsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding-top: 5px;
  overflow: hidden;
  max-height: 60px;
  ${breakpoints.containerLgUp} {
    padding-top: 10px;
  }
  ${breakpoints.mdDown} {
    max-height: 82px;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Tags = ({ color = colors.pinkSalmon, children }: any) => {
  const childrenWithProps = React.Children.map(
    children,
    (child) => child && React.cloneElement(child, { color })
  );
  return <TagsContainer>{childrenWithProps}</TagsContainer>;
};

Tags.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string,
};

export const WineCardPanelBottom = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  bottom: 10px;
  width: calc(100% - 20px);
`;

export const WineCardPanelLeft = styled.div`
  display: flex;
  height: 100%;
  justify-content: space-between;
  align-items: center;
  max-width: 90px;
  width: 100%;
  ${breakpoints.xsDown} {
    width: 40%;
  }
`;

export const WineCardPanelRight = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
`;

export const WineImageHolder = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  float: left;
  padding-bottom: 100%;
`;

export const WineCardImg = styled.img`
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  cursor: pointer;
`;

export const WineCardPreviewImg = styled(WineCardImg)`
  ${breakpoints.xsDown} {
    min-height: 130px;
    object-fit: cover;
  }
`;

export const WineCardPrice = styled.span`
  font-size: 25px;
  line-height: 27px;
  display: inline-block;
  font-family: ${fonts.fontInterstateBlackCompressed};
  ${breakpoints.smDown} {
    font-size: 23px;
    line-height: 26px;
  }
  ${breakpoints.xsDown} {
    font-size: 19px;
    line-height: 22px;
  }
`;

export const BookmarkContainer = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20;
  position: absolute;
  top: 8px;
  left: 8px;
  cursor: pointer;
  background-color: ${colors.whiteTransparent};
`;

export const MemberInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

export const WineRatingInfo = styled(WineRating)`
  display: inline-flex;
  margin-left: -2px;
  padding-top: 2px;
`;
